import React, { useState, useCallback, useEffect } from "react";
import { MathJax, MathJaxContext } from "better-react-mathjax";

const mathJaxConfig = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
  },
};
// waitForMathJax.js (or inline in your component file)
export function waitForMathJax() {
  return new Promise((resolve, reject) => {
    const ready = () =>
      window.MathJax?.startup?.promise?.then(() => resolve()).catch(reject);

    if (window.MathJax) return ready();

    // Poll until MathJax appears
    const id = setInterval(() => {
      if (window.MathJax) {
        clearInterval(id);
        ready();
      }
    }, 100);

    // Safety timeout
    setTimeout(() => {
      clearInterval(id);
      reject(new Error("MathJax not found on window within timeout"));
    }, 10000);
  });
}

const DEFAULT_LATEX =
  "A plane electromagnetic wave propagating in x-direction is described by $E_y = (200 , \\text{Vm}^{-1})\\sin[1.5 \\times 10^7 t - 0.05 x]$. The intensity of the wave is (Use $\\epsilon_0 = 8.85 \\times 10^{-12} , \\text{C}^2\\text{N}^{-1}\\text{m}^{-2}$).";

const LatexRendering = () => {
  const [inputValue, setInputValue] = useState(DEFAULT_LATEX);
  const [renderedLatex, setRenderedLatex] = useState(DEFAULT_LATEX);

  const handleRenderClick = useCallback(() => {
    setRenderedLatex(inputValue.trim());
  }, [inputValue]);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleRenderClick();
    }
  };
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    waitForMathJax()
      .then(() => {
        console.log("MathJAX loaded successfully.");
        setLoaded(true);
      })
      .catch((e) => {
        console.error(e);
        setLoaded(false);
      });
  }, []);
  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-gray-900">
            LaTeX Previewer
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Write LaTeX between <code>$...$</code> for inline math or{" "}
            <code>$$...$$</code> for display equations. Press the button or use{" "}
            <kbd className="px-1 py-0.5 border border-gray-300 rounded">
              Ctrl
            </kbd>{" "}
            +{" "}
            <kbd className="px-1 py-0.5 border border-gray-300 rounded">
              Enter
            </kbd>{" "}
            to render.
          </p>
        </header>

        <div className="space-y-3">
          <label
            htmlFor="latex-input"
            className="block text-sm font-medium text-gray-700"
          >
            LaTeX input
          </label>
          <textarea
            id="latex-input"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            rows={8}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono leading-6 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
            placeholder="Type LaTeX here..."
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleRenderClick}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Render
            </button>
            <span className="text-xs text-gray-500">
              Rendering is manual to keep typing smooth.
            </span>
          </div>
        </div>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Rendered output
          </h2>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-inner">
            {renderedLatex ? (
              <MathJax dynamic>
                <div className="text-base text-gray-900 whitespace-pre-line">
                  {renderedLatex}
                </div>
              </MathJax>
            ) : (
              <p className="text-sm text-gray-400">
                Nothing to show yet. Enter LaTeX above and render it.
              </p>
            )}
          </div>
        </section>
      </div>
    </MathJaxContext>
  );
};

export default LatexRendering;
